import {
  CheckOutlined,
  CopyOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";

import React, { useRef, ReactNode } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Flex } from "./Flex";
import { CopyButton } from "./CopyButton";

export type MarkdownActions = {
  icon: ReactNode;
  title?: string;
  menu: MenuProps;
};
interface MarkdownRendererProps {
  copyEnabled?: boolean;
  header?: ReactNode | string;
  actions?: MarkdownActions[];
  extra?: ReactNode;
  downloadEnabled?: boolean;
  downloadFilename?: string;
  stripBackticksEnabled?: boolean;
  emptyText?: string;
  emptyElement?: ReactNode;
  children: ReactNode;
  className?: string;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  copyEnabled,
  header,
  actions,
  extra,
  downloadEnabled,
  downloadFilename,
  stripBackticksEnabled,
  emptyText,
  children,
  className,
}) => {
  const markdownRef = useRef<HTMLDivElement>(null);
  const BUTTON_STYLES = {
    border: 0,
  };
  const stripBackticks =
    stripBackticksEnabled === undefined || stripBackticksEnabled === null
      ? true
      : stripBackticksEnabled;
  const renderedText = stripBackticks
    ? stripTripleBackticks(children?.toString())
    : children?.toString();

  const markdownHeader = header ? `# ${header}\n` : "";

  const emptyTextToDetect =
    emptyText || "none of the queries returned any data";
  const isEmpty = renderedText == emptyTextToDetect;

  return (
    <div>
      <div ref={markdownRef}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          skipHtml={true}
          className={`vzblMarkDown ${className || ""}`}
          components={{
            a: ({ href, children, ...props }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              >
                {children}
              </a>
            ),
          }}
        >
          {`${markdownHeader}${renderedText}`}
        </Markdown>
      </div>
      <Flex
        style={{
          justifyContent: "space-between",
          padding: 0,
          marginBottom: 20,
        }}
      >
        {copyEnabled && (
          <Tooltip title="Copy to clipboard">
            <span>
              <CopyButton
                text={markdownRef?.current?.innerHTML || ""}
                icon={<CopyOutlined />}
                clickedIcon={<CheckOutlined />}
                style={BUTTON_STYLES}
                disabled={isEmpty}
              />
            </span>
          </Tooltip>
        )}
        <Flex gap={8}>
          {downloadEnabled && (
            <Tooltip title="Download">
              <Button
                icon={<DownloadOutlined />}
                size="small"
                style={BUTTON_STYLES}
                disabled={isEmpty}
                onClick={async () => {
                  const { default: html2pdf } = await import("html2pdf.js");
                  await html2pdf()
                    .set({
                      margin: 0.5,
                      filename: `${downloadFilename}.pdf` || "vzbl_report.pdf",
                      image: { type: "jpeg", quality: 0.98 },
                      html2canvas: {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                      },
                      jsPDF: {
                        unit: "in",
                        format: "letter",
                        hotfixes: ["px_scaling"],
                        orientation: "portrait",
                      },
                      enableLinks: true,
                    })
                    .from(markdownRef.current as HTMLElement)
                    .save();
                }}
              ></Button>
            </Tooltip>
          )}

          {actions &&
            actions.map((action, idx) => (
              <Dropdown key={idx} menu={action.menu} placement="topRight">
                <Button
                  onClick={(e) => e.preventDefault()}
                  icon={action.icon}
                  size="small"
                  style={BUTTON_STYLES}
                >
                  {action.title}
                </Button>
              </Dropdown>
            ))}
          {extra}
        </Flex>
      </Flex>
    </div>
  );
};

const stripTripleBackticks = (input?: string): string => {
  if (input === undefined) {
    return "";
  }
  const trimmed = input.trim();
  // Check if the string starts and ends with triple backticks
  if (trimmed.startsWith("```markdown") && trimmed.endsWith("```")) {
    // Remove the first and last three characters
    return trimmed.slice(11, -3).trim();
  }
  if (trimmed.startsWith("```") && trimmed.endsWith("```")) {
    // Remove the first and last three characters
    return trimmed.slice(3, -3).trim();
  }
  // Return the original string if no triple backticks are found
  return trimmed;
};
